// Angular + External
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChange,
  SimpleChanges,
} from "@angular/core";

import { LoggerService } from "@bfx/logger";
import { TranslateService } from "@ngx-translate/core";
import { IActionCallout } from "app/bfx-element-library/models/CalloutModel";
import {
  ExecutionMonitorLabels,
  LABEL_KEYS,
  ViewDataModel,
} from "app/bfx-execution-monitor/bfx-execution-monitor.const";
import {
  ExecutionMonitoringCmd,
  ExecutionMonitoringCmdPayload,
} from "app/bfx-execution-monitor/models/ExecutionMonitoringCmdPayload";
import {
  EMPTY_AMOUNT_STR,
  OrderExecutionModel,
} from "app/bfx-execution-monitor/models/OrderExecutionModel";
import { BfxExecutionMonitorService } from "app/bfx-execution-monitor/services/bfx-execution-monitor.service";
import { Subject, Subscription, debounce, timer } from "rxjs";
import { BigNumber, OrderIntent, OrderState } from "shared/cubiq-model-multi-asset";

const logger = LoggerService.for(__dirname);

/** # of ms to debounce between changing order state descriptions */
const DEBOUNCE_STATE_CHANGE_MS = 300;
/** # of ms to debounce before processing and pushing out new callouts */
const DEBOUNCE_CALLOUT_CHANGE_MS = 500;

/** # of ms to debounce before processing and pushing out new callouts when order is in an extnerla or rejcted state*/
const EXTERNAL_DEBOUNCE_CALLOUT_CHANGE_MS = 2000;
/**
 * Component for monitoring and quickly operating on an order - it will subscribe to order execution model changes and
 * display those changes in an independent view.
 *
 * Currently, it only requires an input orderId string to start monitoring.
 *
 */
@Component({
  selector: "bfx-execution-monitor",
  templateUrl: "./html/bfx-execution-monitor.html",
  standalone: false,
})
export class BfxExecutionMonitorComponent implements OnInit, OnDestroy, OnChanges {
  /** Model use for view */
  public data: ViewDataModel = {
    totalExecutedLabel: null,
    totalExecuted: null,
    totalRemaining: null,
    prePipsPricePart: null,
    pipsPricePart: null,
    postPipsPricePart: null,

    orderState: null,
    panelIntent: null,

    buttonEnabled: null,
    buttonVisible: null,
    progressBar: { value: 0, label: "Filled", valueStr: null },

    useCallout: false,

    hasVisibility: true,
  };

  public labels: ExecutionMonitorLabels = {
    buttons: {
      info: "Open Order Details",
      modify: "Modify",
      pause: "Pause",
      resume: "Resume",
      fillNow: "Fill Now",
      cancel: "Cancel",
    },
    totals: {
      totalBuy: "Total bought",
      totalSell: "Total sold,",
      totalRemaining: "Remaining",
      percentFilled: "% filled",
    },
  };

  /** ID of order we are monitoring */
  @Input() public orderId: string;

  /** Should this component manage its own callouts? If not you can subscribe to the callout stream with onCalloutChange*/
  @Input() public useLocalCallout: boolean;

  /** Should we enable Execution Monitoring? When false, all monitoring is disabled*/
  @Input() public monitoringEnabled: boolean = true;

  /** Stream of callout events generated by this component e.g. Pause Requested etc These are already translated */
  @Output() onCalloutChange: EventEmitter<IActionCallout> = new EventEmitter();

  /** Local model for exection used for all service operations */
  private model: OrderExecutionModel;

  /** sSubscription for incoming model change events */
  private managerSubscription: Subscription;

  private orderStateSubject: Subject<{ state: string; intent: string }>;
  private actionCalloutSubject: Subject<OrderExecutionModel>;

  private newSubscription: boolean = false;

  constructor(
    private monitoringService: BfxExecutionMonitorService,
    private translate: TranslateService,
    private cd: ChangeDetectorRef,
  ) {
    this.clearModel();
  }

  ngOnInit(): void {
    this.initialize();
  }

  ngOnDestroy(): void {
    this.unmonitorOrder(this.orderId);
    this.unsubscribeToManager();
  }

  ngOnChanges(changes: SimpleChanges): void {
    for (const propName in changes) {
      const change: SimpleChange = changes[propName];
      switch (propName) {
        case "orderId":
          if (!change.firstChange) {
            // firstChange will be handled in ngOnInit() which is called after this
            this.unmonitorOrder(change.previousValue);
            this.monitorOrder(change.currentValue);
          }
          break;
        case "useLocalCallout":
          if (typeof change.currentValue == "string") {
            this.useLocalCallout = change.currentValue.toLowerCase().trim() == "true";
          }
          break;
        case "monitoringEnabled":
          if (!change.firstChange) {
            this.setMonitoringEnabled(this.monitoringEnabled);
          }
          break;
      }
    }
  }

  protected initialize(): void {
    this.orderStateSubject = new Subject();
    // Update order state changes after quiet
    this.orderStateSubject
      .pipe(debounce(() => timer(DEBOUNCE_STATE_CHANGE_MS)))
      .subscribe((stateData) => {
        this.data.orderState = stateData.state;
        this.data.panelIntent = stateData.intent;
      });

    this.actionCalloutSubject = new Subject();
    // Update order state changes after quiet
    this.actionCalloutSubject
      .pipe(
        debounce((model) => {
          let debounceTime: number;
          if (model.getVisibleActionCallout()?.calloutMessage?.length > 0) {
            switch (model.orderState) {
              case OrderState.REJECTED:
              case OrderState.EXT_ACCEPTANCE_NEEDED:
              case OrderState.EXT_MODIFY_ACCEPTANCE_NEEDED:
              case OrderState.EXT_CANCEL_ACCEPTANCE_NEEDED:
                debounceTime = EXTERNAL_DEBOUNCE_CALLOUT_CHANGE_MS;
                break;
              default:
                debounceTime = DEBOUNCE_CALLOUT_CHANGE_MS;
                break;
            }
          } else {
            debounceTime = 0;
          }
          return timer(debounceTime);
        }),
      )
      .subscribe((model) => this.applyCalloutChange(model.getVisibleActionCallout()));

    this.initializeLabelTranslations();
    this.setMonitoringEnabled(this.monitoringEnabled);
  }

  private setMonitoringEnabled(enabled: boolean) {
    if (enabled && !this.managerSubscription) {
      this.subscribeToManager();
    } else if (!enabled && this.managerSubscription) {
      this.unmonitorOrder(this.orderId);
      this.unsubscribeToManager();
      this.resetButtons();
    }
    this.monitorOrder(this.orderId);
  }

  /**
   * Translate all the labels used in the view
   */
  protected initializeLabelTranslations(): void {
    const mapKeys = (keys, values, args?: any) => {
      for (let key in keys) {
        const translation = this.translate.instant(keys[key], args);
        if (translation) {
          values[key] = translation;
        }
      }
    };
    // For all keys in the keys object, get the label and add to labels object
    mapKeys(LABEL_KEYS.buttons, this.labels.buttons);
    mapKeys(LABEL_KEYS.totals, this.labels.totals);

    // Set up a default for total executed
    this.data.totalExecutedLabel = this.labels.totals.totalBuy;
  }

  /**
   * Setup model change handling from the service
   */
  private subscribeToManager(): void {
    if (this.managerSubscription) {
      this.managerSubscription.unsubscribe();
    }
    this.managerSubscription = this.monitoringService.interfaceCmd$.subscribe(
      (payload: ExecutionMonitoringCmdPayload) => this.processEventPayload(payload),
    );
  }

  private unsubscribeToManager(): void {
    if (this.managerSubscription) {
      this.managerSubscription.unsubscribe();
      this.managerSubscription = null;
    }
  }

  /**
   * Update EM to show order with specified ID and monitor changed, where possible
   * @param orderId
   */
  private monitorOrder(orderId: string): void {
    if (orderId) {
      // If monitoring of order, then get all updates through notifications channel
      if (this.monitoringEnabled) {
        this.monitoringService.subscribeOrder(orderId);
        this.newSubscription = true;
      } else if (orderId) {
        // We have no model but we do have an order so we prime from the service
        // without subscribing for changes
        this.monitoringService.getOrderExecutionModel(this.orderId).then((model) => {
          this.updateFromModel(model);
          this.resetButtons();
        });
      }
    }
  }

  private unmonitorOrder(orderId: string): void {
    if (orderId) {
      this.monitoringService.unsubscribeOrder(orderId);
    }
  }

  /**
   * Process a new model from the service - need to update all labels and button states
   * @param payload
   */
  private processEventPayload(payload: ExecutionMonitoringCmdPayload) {
    logger.out.info(`em-c - (${this.orderId}) - got payload for order ${payload.orderId} `);
    if (payload.orderId == this.orderId) {
      switch (payload.command) {
        case ExecutionMonitoringCmd.UPDATE_MODEL:
          this.data.hasVisibility = true;
          this.updateFromModel(payload.payload);
          break;
        case ExecutionMonitoringCmd.CLEAR_MODEL:
          this.clearModel();
          break;
        case ExecutionMonitoringCmd.ACTION_RESPONSE:
          this.updateCallout(payload.payload);
          break;
        case ExecutionMonitoringCmd.LOST_VISIBILITY:
          this.data.hasVisibility = false;
          this.updateCallout(payload.payload);
          // this.unsubscribeToManager();
          break;
      }
    }
  }

  private updateFromModel(payload: OrderExecutionModel) {
    const newModel: boolean = this.model == null;
    this.model = payload;
    if (payload) {
      this.data.totalExecutedLabel =
        payload.intent == OrderIntent.BUY
          ? this.labels.totals.totalBuy
          : this.labels.totals.totalSell;
      this.data.totalExecuted = payload.totalExecutedStr;
      if (this.data.totalExecuted.length == 0 || this.data.totalExecuted === EMPTY_AMOUNT_STR) {
        this.data.totalExecuted === EMPTY_AMOUNT_STR;
        this.data.prePipsPricePart = "";
        this.data.pipsPricePart = "";
        this.data.postPipsPricePart = "";
      } else {
        this.data.totalExecuted += " @ ";
        this.data.prePipsPricePart = payload.vwapPrePips;
        this.data.pipsPricePart = payload.vwapPips;
        this.data.postPipsPricePart = payload.vwapPostPips;
      }
      this.data.totalRemaining = payload.totalRemainingStr;

      const stateData = { state: payload.orderStateStr, intent: payload.panelIntent };
      if (this.newSubscription) {
        // If we're navigating between strategy legs etc, don't want change delay
        // though still need to push onto subject in case there is something else in there that could overwrite this value
        this.newSubscription = false;
        this.data.orderState = stateData.state;
        this.data.panelIntent = stateData.intent;
      }
      this.orderStateSubject.next(stateData);

      this.data.buttonEnabled = payload.buttonEnabled;
      this.data.buttonVisible = payload.buttonVisible;

      this.data.progressBar.value = payload.percentFilled;
      this.data.progressBar.valueStr = new BigNumber(payload.percentFilled).toFixed(
        1,
        BigNumber.ROUND_DOWN,
      );

      if (newModel) {
        // Component may have been hidden and is not visible again - need to ensure callback
        // stay consistent
        this.updateCallout(this.model);
      }
    } else {
      this.clearModel();
    }
  }

  private clearModel() {
    this.data.totalExecutedLabel = "";
    this.data.totalExecuted = "";
    this.data.totalRemaining = "";
    this.data.prePipsPricePart = "";
    this.data.pipsPricePart = "";
    this.data.postPipsPricePart = "";

    this.data.orderState = "";
    this.data.panelIntent = "";

    this.resetButtons();
    this.data.progressBar.value = 0;
    this.data.progressBar.valueStr = "0";
    this.data.localActionCallout = null;
    this.cd.markForCheck();
  }

  /**
   * Disable all buttons and hide
   */
  private resetButtons(): void {
    this.data.buttonEnabled = {
      modify: false,
      pause: false,
      resume: false,
      fillNow: false,
      cancel: false,
    };
    this.data.buttonVisible = {
      modify: false,
      pause: false,
      resume: false,
      fillNow: false,
      cancel: false,
    };
  }

  private updateCallout(model: OrderExecutionModel) {
    this.actionCalloutSubject.next(model);
  }

  private applyCalloutChange(callout: IActionCallout) {
    if (this.monitoringEnabled) {
      if (this.useLocalCallout) {
        this.data.localActionCallout = callout;
        this.cd.markForCheck();
      }
      this.onCalloutChange.next(callout);
    }
  }

  public onCalloutHidden() {
    this.data.localActionCallout = null;
  }

  public onShowDetails(_event) {
    logger.out.info(
      `ExecutionMonitor - viewDetails requested for order ${this.model.orderId ?? "<Unknown>"}`,
    );
    this.monitoringService.showDetails(this.model);
  }

  public onPause(_event) {
    logger.out.info(
      `ExecutionMonitor - pause requested for order ${this.model.orderId ?? "<Unknown>"}`,
    );
    this.monitoringService.pause(this.model);
  }
  public onResume(_event) {
    logger.out.info(
      `ExecutionMonitor - resume requested for order ${this.model.orderId ?? "<Unknown>"}`,
    );
    this.monitoringService.resume(this.model);
  }
  public onCancel(_event) {
    logger.out.info(
      `ExecutionMonitor - cancel requested for order ${this.model.orderId ?? "<Unknown>"}`,
    );
    this.monitoringService.cancel(this.model);
  }
  public onModify(_event) {
    logger.out.info(
      `ExecutionMonitor - modify requested for order ${this.model.orderId ?? "<Unknown>"}`,
    );
    this.monitoringService.modify(this.model);
  }
  public onFillNow(_event) {
    logger.out.info(
      `ExecutionMonitor - fillNow requested for order ${this.model.orderId ?? "<Unknown>"}`,
    );
    this.monitoringService.fillNow(this.model);
  }
}
